import { Link } from "react-router-dom";
import { User, ArrowRight, Linkedin } from "lucide-react";
import { KineticHeading } from "../components/site/KineticHeading";
import { Reveal } from "../components/site/Reveal";
import { useContent } from "../context/ContentContext";
import { img } from "../lib/api";

export default function About() {
  const { site, team } = useContent();
  const a = site.about || {};
  return (
    <>
      <section className="mx-auto max-w-[1400px] px-6 pb-16 pt-40 md:px-10 md:pt-48" data-testid="about-hero">
        <div className="mb-6 text-xs font-bold uppercase tracking-[0.25em] text-volt">{a.overline}</div>
        <KineticHeading lines={a.headingLines || []}
          className="font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl" />
        <p className="mt-8 max-w-2xl text-lg text-white/60">{a.intro}</p>
      </section>

      <section className="mx-auto max-w-[1400px] border-t border-white/10 px-6 py-24 md:px-10 md:py-32" data-testid="about-name">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="sticky top-32">
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-volt">The name</div>
              <h2 className="font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">Why <span className="text-gradient">KORIO</span>?</h2>
            </div>
          </div>
          <div className="md:col-span-8">
            <Reveal><p className="text-xl leading-relaxed text-white/80 sm:text-2xl">{a.lead}</p></Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <Reveal>
                <div className="h-full rounded-2xl border border-white/10 bg-korio-surface p-7">
                  <div className="font-display text-3xl font-semibold text-gradient">{a.core?.title}</div>
                  <p className="mt-3 text-white/60">{a.core?.body}</p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="h-full rounded-2xl border border-white/10 bg-korio-surface p-7">
                  <div className="font-display text-3xl font-semibold text-gradient">{a.io?.title}</div>
                  <p className="mt-3 text-white/60">{a.io?.body}</p>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}><p className="mt-10 text-lg leading-relaxed text-white/60">{a.body}</p></Reveal>
          </div>
        </div>

        <Reveal>
          <blockquote className="mt-24 border-l-2 border-brand-magenta pl-6 md:pl-10">
            <p className="font-display text-3xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              <span className="text-gradient">{a.quote}</span>
            </p>
          </blockquote>
        </Reveal>
      </section>

      <section className="border-t border-white/10 bg-korio-surface py-24 md:py-32" data-testid="about-team">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mb-16 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-volt">The team</div>
              <h2 className="font-display text-4xl font-medium tracking-tight sm:text-5xl">The people behind the flow.</h2>
            </div>
            {a.teamNote && <p className="max-w-sm text-sm text-white/40">{a.teamNote}</p>}
          </div>

          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {team.map((m, i) => (
              <Reveal key={m.id || i} delay={(i % 4) * 0.08}>
                <div data-testid={`team-member-${i}`} className="group relative aspect-[3/4] cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-korio-elevated">
                  {/* gradient background revealed on hover — sits BEHIND the person */}
                  <div className="brand-gradient pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  {m.photo ? (
                    <img src={img(m.photo)} alt={m.name} className="relative h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                  ) : (
                    <div className="relative flex h-full w-full items-center justify-center text-white/40 transition-transform duration-700 ease-out group-hover:scale-[1.04]"><User size={56} strokeWidth={1.25} /></div>
                  )}
                  {/* bottom scrim for legibility */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  {/* name + role overlay */}
                  <div className="absolute inset-x-0 bottom-0 p-5 transition-transform duration-500 ease-out group-hover:-translate-y-1">
                    <h3 className="font-display text-xl font-medium tracking-tight text-white">{m.name}</h3>
                    <div className="mt-0.5 text-sm font-semibold text-gradient">{m.role}</div>
                  </div>
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noreferrer" data-testid={`team-linkedin-${i}`} aria-label={`${m.name} on LinkedIn`}
                      className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white opacity-0 backdrop-blur transition-all duration-300 hover:bg-volt hover:text-black group-hover:opacity-100">
                      <Linkedin size={18} />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-korio-bg py-24 text-center md:py-32" data-testid="about-cta">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <h2 className="mx-auto max-w-3xl font-display text-4xl font-medium tracking-tight sm:text-5xl">Let's build your next experience together.</h2>
          <Link to="/contact" data-testid="about-cta-button" className="brand-gradient group mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
            Start a project <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </>
  );
}
